/**
 * ### README ###
 *
 * Notification Action Handler
 *
 * This module defines a unified way to handle various notification actions in the app.
 *
 * ## Adding a New Notification Action:
 *
 * 1. Extend the `NotificationAction` enum with a new key:
 *    ```ts
 *    export enum NotificationAction {
 *      NAVIGATE = "navigate",
 *      NEW_ACTION = "newAction",
 *    }
 *    ```
 *
 * 2. Extend the `NotificationExtraMap` interface with a matching payload type:
 *    ```ts
 *    export interface NotificationExtraMap extends Record<NotificationAction, unknown> {
 *      navigate: { to: string; options?: NavigateOptions };
 *      newAction: { someData: string };
 *    }
 *    ```
 *
 * 3. Add handling logic to the `handleAction` function inside `useNotificationAction`.
 */

import { useCallback } from "react";
import { useNavigate, type NavigateOptions } from "react-router-dom";

/**
 * Enum representing supported notification actions.
 */
export enum NotificationAction {
  NAVIGATE = "navigate",
}

/**
 * Maps each NotificationAction to the expected payload type.
 */
export interface NotificationActionExtraMap
  extends Record<NotificationAction, unknown> {
  /**
   * Payload for the 'navigate' action.
   */
  navigate: {
    to: string;
    options?: NavigateOptions;
  };
}

/**
 * A list of all NotificationAction enum values.
 */
export const NOTIFICATION_ACTIONS_KEYS = Object.values(
  NotificationAction,
) as NotificationAction[];

export type NotificationActionPayloads = {
  [K in NotificationAction]: {
    /**
     * Decides which method to evoke based on this ID
     */
    id: K;

    /**
     * Extra data - provided to the method evoked for this action
     */
    extra: NotificationActionExtraMap[K];

    /**
     * The text shown in the action's button
     */
    btnText: string;
  };
}[NotificationAction];

export type NotificationActionHandler = (action: NotificationActionPayloads) => void;

/**
 * Hook returning a function that handles in-app notification actions.
 *
 * @returns A typed function that can be used to perform notification actions.
 */
export function useNotificationAction(): NotificationActionHandler {
  const navigate = useNavigate();

  const handleAction = useCallback<NotificationActionHandler>(
    (action) => {
      switch (action.id) {
        case NotificationAction.NAVIGATE:
          navigate(
            action.extra.to.includes("/event") &&
              !action.extra.to.includes("/events")
              ? action.extra.to.replace("/event", "/events")
              : action.extra.to,
            action.extra.options,
          );
          break;

        default:
          console.warn(`Unhandled notification action: ${action.id}`);
      }
    },
    [navigate],
  );

  return handleAction;
}
